// client/src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import tokenService from '../services/tokenService';
import API_BASE_URL from '../config/api.config';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Set axios authorization header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Add axios interceptor to track activity on API calls
  useEffect(() => {
    if (token && user) {
      // Request interceptor to update activity on API calls
      const requestInterceptor = axios.interceptors.request.use(
        (config) => {
          tokenService.updateActivity();
          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );

      // Cleanup interceptor
      return () => {
        axios.interceptors.request.eject(requestInterceptor);
      };
    }
  }, [token, user]);

  // Start token refresh when user is authenticated
  useEffect(() => {
    if (token && user && initialized) {
      // Start automatic token refresh
      tokenService.startTokenRefresh(
        // Success callback
        (newToken, userData) => {
          setToken(newToken);
          localStorage.setItem('token', newToken);
          
          if (userData) {
            setUser(prevUser => ({
              ...prevUser,
              ...userData
            }));
          }
        },
        // Error callback
        (error) => {
          logout();
        }
      );
    } else {
      // Stop token refresh when user is not authenticated
      tokenService.stopTokenRefresh();
    }

    // Cleanup on unmount
    return () => {
      tokenService.stopTokenRefresh();
    };
  }, [token, user, initialized]);

  // Track user activity globally (but only general activity, not session timeout)
  useEffect(() => {
    if (token && user) {
      const handleUserActivity = () => {
        tokenService.updateActivity();
      };
      
      // Monitor activity for token refresh purposes
      const activityEvents = [
        'mousedown', 'mousemove', 'keypress', 
        'scroll', 'touchstart', 'click'
      ];
      
      // Add event listeners
      activityEvents.forEach(event => {
        window.addEventListener(event, handleUserActivity, true);
      });
      
      // Cleanup
      return () => {
        activityEvents.forEach(event => {
          window.removeEventListener(event, handleUserActivity, true);
        });
      };
    }
  }, [token, user]);

  // Load User
  useEffect(() => {
    let cancelled = false;
    let isLoading = false; // Previene chiamate multiple
    
    const loadUser = async () => {
      // Se stiamo già caricando, esci
      if (isLoading) {
        return;
      }
      
      if (token && !user && !cancelled) {
        isLoading = true; // Imposta flag di caricamento
        setLoading(true);
        
        try {
          const res = await axios.get(`${API_BASE_URL}/auth/profile`);
                   
          if (!cancelled && res.data?.user) {
            setUser(res.data.user);
          }
        } catch (err) {
          if (!cancelled) {
            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
              localStorage.removeItem('token');
              setToken(null);
              setUser(null);
            }
            // Log solo in development
            if (process.env.NODE_ENV === 'development') {
              console.error('Auth error:', err.message);
            }
            // Non esporre altri errori all'utente
          }
        } finally {
          if (!cancelled) {
            isLoading = false;
            setLoading(false);
            setInitialized(true);
          }
        }
      } else if (!token) {
        // Nessun token, imposta stato inizializzato
        if (!cancelled) {
          setLoading(false);
          setInitialized(true);
          setUser(null);
        }
      } else if (user) {
        // User già presente, assicurati che sia inizializzato
        if (!cancelled && !initialized) {
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    loadUser();
    
    return () => {
      cancelled = true;
    };
  }, [token, user, initialized]); // Aggiungi initialized alle dipendenze

  // Login user - returns a Promise to allow waiting for completion
  const login = async (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    
    // Set authorization header immediately
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    
    // Initialize activity time for new session
    tokenService.updateActivity();
    
    // Return a resolved promise to make this awaitable
    return Promise.resolve();
  };

  // Logout user
  const logout = () => {
    
    // Clean up token service first
    tokenService.cleanup();
    
    // Clear local state and storage
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    
  };

  // Update user profile
  const updateProfile = (updatedUser) => {
    setUser({ ...user, ...updatedUser });
  };
  
  // Manual token refresh
  const refreshToken = async () => {
    try {
      const newToken = await tokenService.manualRefresh();
      return newToken;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Manual token refresh failed:', error);
      }
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        updateProfile,
        refreshToken,
        isAuthenticated: !!token,
        initialized
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};